import { ArrowRight, ShieldCheck } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isDemoMode, login, verifyLogin } from "../../lib/api";
import { resolveAuthenticatedLandingRoute } from "../../lib/defaultLandingWorkspace";
import { setDemoAuthRole } from "../../lib/runtimeMode";

export function FormsAdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [message, setMessage] = useState("");
  const [helpText, setHelpText] = useState("Codes may take a few minutes. Check spam or promotions.");
  const [requestedAt, setRequestedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [resendAvailableAt, setResendAvailableAt] = useState("");
  const [deliveryProvider, setDeliveryProvider] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("");
  const [previewDevCode, setPreviewDevCode] = useState("");
  const [clock, setClock] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState("");

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const resendDisabledUntil = useMemo(() => {
    if (!resendAvailableAt) return 0;
    const timestamp = new Date(resendAvailableAt).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }, [resendAvailableAt]);

  const resendCooldownSeconds = Math.max(0, Math.ceil((resendDisabledUntil - clock) / 1000));
  const rateLimitedUntilTimestamp = useMemo(() => {
    if (!rateLimitedUntil) return 0;
    const timestamp = new Date(rateLimitedUntil).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }, [rateLimitedUntil]);
  const rateLimitSeconds = Math.max(0, Math.ceil((rateLimitedUntilTimestamp - clock) / 1000));
  const canRequestCode = !busy && rateLimitSeconds === 0;
  const canResend = step === "code" && canRequestCode && resendCooldownSeconds === 0;
  const deliveryStatusLabel =
    deliveryStatus === "accepted_by_brevo"
      ? "Accepted by Brevo"
      : deliveryStatus === "local_testing"
        ? "Local testing"
        : deliveryStatus
          ? deliveryStatus.replaceAll("_", " ")
          : "";

  useEffect(() => {
    if (rateLimitedUntil && rateLimitSeconds === 0) {
      setRateLimitedUntil("");
      setMessage((currentMessage) => currentMessage.includes("Please try again in") ? "" : currentMessage);
    }
  }, [rateLimitedUntil, rateLimitSeconds]);

  function getRetrySecondsFromMessage(value: string) {
    const match = value.match(/try again in\s+(\d+)\s+seconds/i);
    return match ? Number(match[1]) || 0 : 0;
  }

  function clearLoginStatus() {
    setMessage("");
    setRateLimitedUntil("");
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    clearLoginStatus();
    if (step === "code") {
      setCode("");
      setStep("email");
      setRequestedAt("");
      setExpiresAt("");
      setDeliveryProvider("");
      setDeliveryStatus("");
      setPreviewDevCode("");
      setResendAvailableAt("");
      setHelpText("Codes may take a few minutes. Check spam or promotions.");
    }
  }

  function handleCodeChange(value: string) {
    setCode(value);
    setMessage("");
  }

  function applyChallengeResponse(result: {
    message: string;
    devCode?: string;
    requestedAt?: string;
    expiresAt?: string;
    deliveryStatus?: string;
    deliveryProvider?: string;
    helpText?: string;
    resendAvailableAt?: string;
  }) {
    setMessage(result.message);
    setHelpText(result.helpText || "Codes may take a few minutes. Check spam or promotions.");
    setRequestedAt(result.requestedAt || "");
    setExpiresAt(result.expiresAt || "");
    setDeliveryProvider(result.deliveryProvider || "");
    setDeliveryStatus(result.deliveryStatus || "");
    setPreviewDevCode(result.devCode || "");
    setResendAvailableAt(result.resendAvailableAt || "");
    setRateLimitedUntil("");
    setStep("code");
  }

  async function requestCode(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    if (!canRequestCode) return;
    setBusy(true);
    setMessage("");
    setCode("");
    setPreviewDevCode("");

    try {
      const result = await login(email.trim(), true);
      applyChallengeResponse(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unable to complete admin login.";
      const retrySeconds = getRetrySecondsFromMessage(errorMessage);
      if (retrySeconds > 0) {
        const retryAt = new Date(Date.now() + retrySeconds * 1000).toISOString();
        setRateLimitedUntil(retryAt);
        setResendAvailableAt(retryAt);
      }
      setMessage(errorMessage || "We could not send your access code. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step === "email") {
      await requestCode();
      return;
    }

    setBusy(true);
    setMessage("");

    try {
      const session = await verifyLogin(email.trim(), code, true);
      const landingRoute = await resolveAuthenticatedLandingRoute({
        email: session.user.email,
        role: session.user.role,
      });
      setExiting(true);
      window.setTimeout(() => navigate(landingRoute, { replace: true }), 220);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to complete admin login.");
    } finally {
      setBusy(false);
    }
  }

  function previewAdmin() {
    // Local preview should work even when strict backend mode is enabled for normal dev testing.
    // This stays browser-only and never runs in production builds.
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("ktd_demo_auth", "admin");
        window.localStorage.setItem("ktd_runtime_mode", "demo");
      }
    } catch {
      // Ignore storage failures in hardened/private browsing contexts.
    }
    setDemoAuthRole("admin");
    navigate("/admin/business-center-os", { replace: true });
  }

  return (
    <main className="collab-login-shell min-h-screen overflow-hidden px-5 py-8 sm:px-6">
      <div className="collab-login-bg-grid" aria-hidden="true" />
      <div className="collab-login-ambient collab-login-ambient-orange" aria-hidden="true" />
      <div className="collab-login-ambient collab-login-ambient-teal" aria-hidden="true" />
      <div className="collab-login-ambient collab-login-ambient-blue" aria-hidden="true" />

      <section className="collab-login-experience mx-auto grid min-h-[calc(100dvh-4rem)] w-full max-w-6xl items-center gap-10 lg:grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)] lg:gap-14">
        <div className="collab-login-brand-core text-center lg:text-left">
          <span className="collab-login-command-pill mb-5 inline-flex items-center gap-2">
            <span className="collab-login-command-dot" aria-hidden="true" />
            AR Recovery Command Access
          </span>
          <span className="collab-login-badge mx-auto grid place-items-center overflow-hidden text-lg font-black lg:mx-0">
            <img
              src="/assets/collabrecover-cr-logo.png"
              alt="CollabRecover AR Solutions"
              className="h-full w-full rounded-[inherit] object-cover"
            />
          </span>
          <h1 className="collab-login-title mx-auto mt-6 text-balance font-display font-bold leading-[0.98] lg:mx-0">
            <span className="block">CollabRecover</span>
            <span className="block">AR Solutions</span>
          </h1>
          <p className="collab-login-tagline mt-5 font-semibold">Turn Recovery Into Cash Flow</p>
          <p className="collab-login-confidence-copy mx-auto mt-5 max-w-md text-sm font-semibold leading-7 lg:mx-0">
            Secure access for account recovery teams managing customers, documents, promises, and recovery activity.
          </p>
          <div className="collab-login-proof-row mt-7 flex flex-wrap justify-center gap-2 lg:justify-start">
            <span>No System Access Required</span>
            <span>Encrypted One-Time Code</span>
            <span>Enterprise Protected</span>
          </div>
        </div>

        <section className="collab-login-access-card w-full max-w-[520px] p-6 sm:p-10 lg:ml-auto" data-step={step} data-exiting={exiting ? "true" : "false"}>
          <div className="collab-login-card-chrome mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <p>CollabRecover Access</p>
          </div>
          <Link to="/" className="focus-ring collab-login-secure-chip inline-flex items-center gap-2 px-4 py-2 text-sm font-bold transition">
            <ShieldCheck className="size-4" aria-hidden="true" />
            Secure Access
          </Link>

          <div className="mt-7">
            <h2 className="text-balance font-display text-3xl font-bold sm:text-4xl">Secure Access</h2>
            <p className="collab-login-overline mt-3 text-xs font-extrabold uppercase tracking-[0.18em]">Welcome Back</p>
            <p className="mt-2 text-sm font-semibold leading-6 text-white/62">Sign in to CollabRecover AR Solutions</p>
          </div>

          <form onSubmit={submit} className="mt-7 grid gap-4">
            <label className="collab-login-email-field grid gap-2 text-sm font-bold text-white/86">
              Email Address
              <input
                type="email"
                required
                value={email}
                onChange={(event) => handleEmailChange(event.target.value)}
                className="form-field collab-login-input focus-ring rounded-2xl px-4 py-3 font-medium"
                placeholder="admin@example.com"
              />
            </label>

            {step === "code" ? (
              <div className="grid gap-3">
                <label className="collab-login-code-field grid gap-2 text-sm font-bold text-white/86">
                  One-Time Code
                  <input
                    inputMode="numeric"
                    required
                    value={code}
                    onChange={(event) => handleCodeChange(event.target.value)}
                    className="form-field collab-login-input focus-ring rounded-2xl px-4 py-3 text-lg font-bold tracking-[0.2em]"
                    placeholder="000000"
                  />
                </label>

                <div className="collab-login-info-panel grid gap-2 rounded-2xl p-4 text-sm font-semibold">
                  <p>{helpText}</p>
                  <p>Recovery code support is available when provisioned by an administrator.</p>
                  {previewDevCode ? (
                    <div className="rounded-2xl border border-[#22E0D0]/45 bg-[#22E0D0]/10 p-4 text-white" role="status" aria-live="polite">
                      <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#22E0D0]">Preview Testing Code</p>
                      <p className="mt-2 font-mono text-3xl font-black tracking-[0.28em] text-white">{previewDevCode}</p>
                      <p className="mt-2 text-xs font-semibold leading-5 text-white/70">Visible only when the Preview environment returns a local testing code.</p>
                    </div>
                  ) : null}
                  {requestedAt ? <p>Code requested at: {new Date(requestedAt).toLocaleString()}</p> : null}
                  {expiresAt ? <p>Code expires at: {new Date(expiresAt).toLocaleString()}</p> : null}
                  {deliveryProvider ? (
                    <p>
                      Delivery status: {deliveryStatusLabel || "Sent"} via {deliveryProvider}
                    </p>
                  ) : null}
                  {resendCooldownSeconds > 0 ? <p>Resend available in {resendCooldownSeconds} seconds.</p> : null}
                  {rateLimitSeconds > 0 ? <p>Login requests reopen in {rateLimitSeconds} seconds.</p> : null}
                </div>
              </div>
            ) : null}

            {message ? (
              <p className="collab-login-status-panel rounded-2xl p-4 text-sm font-semibold leading-6" role="status">
                {message}
              </p>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={step === "email" ? !canRequestCode : busy}
                className="focus-ring collab-login-primary-button inline-flex min-h-12 w-full flex-1 items-center justify-center gap-2 px-6 py-3 text-sm font-extrabold transition disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <span className="collab-login-spinner" aria-hidden="true" />
                    {step === "email" ? "Sending Secure Code..." : "Checking Access..."}
                  </>
                ) : (
                  <>
                    {step === "email" && rateLimitSeconds > 0 ? `Try Again in ${rateLimitSeconds}s` : step === "email" ? "Send Access Code" : "Login to CollabRecover"}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </>
                )}
              </button>

              {step === "code" ? (
                <button
                  type="button"
                  onClick={() => requestCode()}
                  disabled={!canResend}
                  className="focus-ring collab-login-secondary-button inline-flex min-h-12 w-full flex-1 items-center justify-center px-5 py-3 text-sm font-extrabold transition disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy
                    ? "Resending..."
                    : rateLimitSeconds > 0
                      ? `Try Again in ${rateLimitSeconds}s`
                      : resendCooldownSeconds > 0
                        ? `Resend in ${resendCooldownSeconds}s`
                        : "Resend Code"}
                </button>
              ) : null}
            </div>

            <div className="collab-login-trust-row grid grid-cols-3 gap-2 text-center text-[11px] font-extrabold uppercase tracking-[0.12em] text-white/62">
              <span>Secure</span>
              <span>One-Time Code</span>
              <span>Protected</span>
            </div>

            {import.meta.env.DEV || isDemoMode ? (
              <button
                type="button"
                onClick={previewAdmin}
                className="focus-ring collab-login-secondary-button rounded-full px-5 py-3 text-sm font-extrabold"
              >
                Preview Business Center OS Locally
              </button>
            ) : null}
          </form>
        </section>
      </section>
    </main>
  );
}
